import { useNavigate } from 'react-router-dom';
import { useCreateData, useFetchData, useUserStore } from '../../hooks';
import { Button, Form, Space } from 'antd';
import InfoModal from '../../components/ui/modal/InfoModal';
import ErrorModal from '../../components/ui/modal/ErrorModal';
import ModulePermissionForm from './ModulePermissionForm';
import React, { useEffect, useState } from 'react';
import { ModuleAccess } from '../../types/auth.model';
import {
  BaseModel,
  DepartmentModel,
  FormField,
  PageableResponse,
  UserFormModel,
} from '../../types';
import PageHeaderAction from '../../components/PageHeaderAction';
import SubmitButton from '../../components/ui/SubmitButton';
import CustomForm from '../../components/ui/CustomForm';
import { Resource, UserRole } from '../../lib/enums';

const UserFormCreate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const userStore = useUserStore();
  const api = `/users`;

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  useEffect(() => {
    form.resetFields();
  }, []);
  const handleClose = () => {
    navigate(-1);
    setIsCanceled(true);
    form.resetFields();
  };

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<DepartmentModel>>('/hr/departments', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const roleOptions: BaseModel[] = Object.keys(UserRole).map(
    (key) =>
      ({
        id: key,
        code: UserRole[key as keyof typeof UserRole],
        description: UserRole[key as keyof typeof UserRole],
      }) as BaseModel,
  );

  const [selectedModules, setSelectedModules] = useState<ModuleAccess[]>([]);
  const [isCanceled, setIsCanceled] = useState(false);

  const { data: moduleList } = useFetchData<PageableResponse<BaseModel>>(
    '/app/modules',
    {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    },
  );

  const {
    mutate,
    isError: isUpdateError,
    error: updateError,
    isSuccess,
  } = useCreateData<UserFormModel>(api);

  const onFinish = (values: {
    department: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
  }) => {
    const location: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.department;
      },
    );

    const moduleAccess: ModuleAccess[] = (moduleList?.data || []).map(
      (module) => {
        const moduleAccess = userStore.data?.moduleAccess?.find(
          (access) => access.module.id === module.id,
        );

        const initialPermissions = {
          create: moduleAccess?.create ?? false,
          read: moduleAccess?.read ?? false,
          write: moduleAccess?.write ?? false,
          delete: moduleAccess?.delete ?? false,
          approve: moduleAccess?.approve ?? false,
          confirm: moduleAccess?.confirm ?? false,
          available: moduleAccess?.available ?? false,
          issuannce: moduleAccess?.issuannce ?? false,
          cancel: moduleAccess?.cancel ?? false,
        };

        const selectedPermissions = selectedModules.find(
          (mod) => mod.id === module.id,
        );

        return {
          ...initialPermissions,
          ...(selectedPermissions || {}),
          module: {
            id: module.id,
            code: module.code,
            description: module.description,
          },
          id: null,
          logRevisionId: null,
          createdBy: undefined,
          updatedBy: undefined,
          timeCreated: undefined,
          timeUpdated: undefined,
          close: false, // Add the close property with a default value
          status: null, // Assuming status is nullable
        };
      },
    );

    const jsonValues = {
      id: undefined,
      username: values.username,
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
      email: values.email,
      department: location,
      status: 'ACTIVE',
      role: values.role,
      createdBy: userStore.data?.createdBy,
      updatedBy: userStore.data?.updatedBy,
      timeCreated: userStore.data?.timeCreated,
      timeUpdated: userStore.data?.timeUpdated,
      moduleAccess,
    };
    mutate(jsonValues);
  };

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isUpdateError) {
      setIsErrorModalOpen(true);
      setErrorMess(updateError?.response?.data.message);
    }
  }, [isSuccess, isUpdateError]);

  const handleModuleSelection = (moduleId: string, permissions: any) => {
    setSelectedModules((prevModules) => {
      const index = prevModules.findIndex((mod) => mod.id === moduleId);
      if (index === -1) {
        return [
          ...prevModules,
          {
            id: moduleId,
            module: moduleList?.data.find(
              (mod: any) => mod.id === moduleId,
            ) || {
              id: moduleId,
              name: '',
            },
            ...permissions,
          },
        ];
      } else {
        const updatedModules = [...prevModules];
        updatedModules[index] = {
          ...updatedModules[index],
          ...permissions,
        };
        return updatedModules;
      }
    });
  };

  const fields: FormField[] = [
    {
      name: 'username',
      label: 'Username',
      rules: [{ required: true, message: 'Please input username' }],
      type: 'input',
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      rules: [{ type: 'email' }],
    },
    {
      name: 'firstName',
      label: 'First Name',
      type: 'input',
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'input',
    },
    {
      name: 'department',
      label: 'Department',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select Department' }],
    },
    {
      name: 'role',
      label: 'Role',
      allowClear: false,
      type: 'select',
      isLoading: false,
      isMultiple: false,
      values: roleOptions,
      rules: [{ required: true, message: 'Please select role' }],
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      rules: [
        { required: true, message: 'Please enter password' },
        { min: 6, message: 'Password must be at least 6 characters' }, // Minimum length validation
      ],
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'confirmPassword',
    },
  ];

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    handleClose();
  };

  return (
    <>
      <div className={'flex items-center justify-between '}>
        <PageHeaderAction action={'Request'} moduleName={'User'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={false} form={form}>
            Create
          </SubmitButton>
        </Space>
      </div>
      <div className=" h-[calc(84vh)] overflow-auto ">
        <div className="p-4 bg-white mt-4 rounded-xl">
          <CustomForm
            data={userStore.data}
            formFields={fields}
            form={form}
            onFinish={onFinish}
            rowCol={2}
            layout={'vertical'}
            formName={'po_form'}
          />
        </div>

        <div className="sticky top-0 z-10">
          <div className="flex flex-col space-y-4 p-4 bg-white mt-4 rounded-t ">
            <span className="font-semibold text-lg">Permissions</span>
            <div className="flex flex-row justify-between bg-gray-200 p-4">
              <span className="w-2/6 font-semibold">Module</span>
              <span className="w-1/6 font-semibold">Read</span>
              <span className="w-1/6 font-semibold">Create</span>
              <span className="w-1/6 font-semibold">Write</span>
              <span className="w-1/6 font-semibold">Delete</span>
              <span className="w-1/6 font-semibold">Approved</span>
              <span className="w-1/6 font-semibold">Confirm</span>
              <span className="w-1/6 font-semibold">Available</span>
              <span className="w-1/6 font-semibold">Issuance</span>
              <span className="w-1/6 font-semibold">Cancel</span>
            </div>
          </div>
        </div>

        <div className="overflow-auto bg-white rounded-b">
          {moduleList?.data.map((module) => (
            <ModulePermissionForm
              key={module.id}
              moduleName={module.description}
              onPermissionChange={(permissions) =>
                handleModuleSelection(module.id, permissions)
              }
              selectedPermissions={selectedModules.find(
                (mod) => mod.id === module.id,
              )}
              reset={isCanceled}
            />
          ))}
        </div>
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'User successfully Created'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default UserFormCreate;
